import { httpClient } from "../http/client";
import { spotViewerAuthorizationHeader } from "../constants/spotViewerAuth";

export interface SpotTickerItem {
  collegeId: string;
  college_name: string;
  total_available_seats: number;
  college_code: string;
}

export type SpotTickerQueryType = "All" | "ByCollegeIds";

type UnknownRecord = Record<string, unknown>;

const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = 20;

function asRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === "object" ? (value as UnknownRecord) : null;
}

function readString(record: UnknownRecord | null, keys: string[]): string {
  if (!record) {
    return "";
  }
  for (const key of keys) {
    const raw = record[key];
    if (typeof raw === "string" && raw.trim()) {
      return raw.trim();
    }
    if (typeof raw === "number" && Number.isFinite(raw)) {
      return String(raw);
    }
  }
  return "";
}

function readNumber(record: UnknownRecord | null, keys: string[]): number {
  if (!record) {
    return 0;
  }
  for (const key of keys) {
    const raw = record[key];
    if (typeof raw === "number" && Number.isFinite(raw)) {
      return raw;
    }
    if (typeof raw === "string" && raw.trim()) {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return 0;
}

function readArray(record: UnknownRecord | null, keys: string[]): unknown[] {
  if (!record) {
    return [];
  }
  for (const key of keys) {
    const raw = record[key];
    if (Array.isArray(raw)) {
      return raw;
    }
  }
  return [];
}

function normalizeTickerItem(item: unknown): SpotTickerItem | null {
  const record = asRecord(item);
  if (!record) {
    return null;
  }

  const collegeName = readString(record, ["college_name", "collegeName", "name"]);
  if (!collegeName) {
    return null;
  }

  const collegeId = readString(record, [
    "college_id",
    "collegeId",
    "CollegeId",
    "instituteId",
    "institute_id",
  ]);
  if (!collegeId) {
    return null;
  }

  return {
    collegeId,
    college_name: collegeName,
    total_available_seats: readNumber(record, [
      "total_available_seats",
      "totalAvailableSeats",
      "available_seats",
      "availableSeats",
      "seats",
    ]),
    college_code: readString(record, ["college_code", "collegeCode", "code"]),
  };
}

function extractTickerItems(raw: unknown): SpotTickerItem[] {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => normalizeTickerItem(item))
      .filter((item): item is SpotTickerItem => item !== null);
  }

  const root = asRecord(raw);
  if (!root) {
    return [];
  }

  const nestedCandidates = [
    root,
    asRecord(root.data),
    asRecord(root.result),
    asRecord(root.payload),
    asRecord(root.response),
  ];

  for (const candidate of nestedCandidates) {
    const items = readArray(candidate, ["content", "items", "rows", "data", "colleges", "results"]);
    if (items.length > 0) {
      return items
        .map((item) => normalizeTickerItem(item))
        .filter((item): item is SpotTickerItem => item !== null);
    }
  }

  return [];
}

function getSpotTickerErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "response" in error) {
    const axiosLike = error as {
      response?: { data?: unknown; status?: number };
      message?: string;
    };
    const data = axiosLike.response?.data;

    if (typeof data === "string" && data.trim()) {
      return data.trim();
    }

    if (data && typeof data === "object") {
      const typed = data as { message?: unknown; detail?: unknown; error?: unknown };
      if (typeof typed.message === "string" && typed.message.trim()) {
        return typed.message.trim();
      }
      if (typeof typed.detail === "string" && typed.detail.trim()) {
        return typed.detail.trim();
      }
      if (typeof typed.error === "string" && typed.error.trim()) {
        return typed.error.trim();
      }
    }

    if (axiosLike.response?.status === 401 || axiosLike.response?.status === 403) {
      return "Unable to load Spot Round ticker data. Please try again later.";
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return "Unable to load Spot Round ticker data. Please try again later.";
}

async function exportSpotTicker(
  queryType: SpotTickerQueryType,
  body: Record<string, unknown>,
  pageNumber: number = DEFAULT_PAGE_NUMBER,
  pageSize: number = DEFAULT_PAGE_SIZE,
): Promise<SpotTickerItem[]> {
  try {
    const response = await httpClient.post<unknown>(
      `/admin/exporter/exportMultiple/SpotTickerExporter/${queryType}/${pageNumber}/${pageSize}`,
      body,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: spotViewerAuthorizationHeader(),
        },
      },
    );
    return extractTickerItems(response.data);
  } catch (error) {
    throw new Error(getSpotTickerErrorMessage(error));
  }
}

export async function fetchAllCollegesTicker(
  pageNumber: number = DEFAULT_PAGE_NUMBER,
  pageSize: number = DEFAULT_PAGE_SIZE,
): Promise<SpotTickerItem[]> {
  return exportSpotTicker("All", {}, pageNumber, pageSize);
}

export async function fetchShortlistedCollegesTicker(
  collegeIds: number[],
  pageNumber: number = DEFAULT_PAGE_NUMBER,
  pageSize: number = DEFAULT_PAGE_SIZE,
): Promise<SpotTickerItem[]> {
  const uniqueIds = Array.from(
    new Set(
      collegeIds.filter((id) => typeof id === "number" && Number.isFinite(id) && id > 0),
    ),
  );

  if (uniqueIds.length === 0) {
    return [];
  }

  return exportSpotTicker(
    "ByCollegeIds",
    { college_ids: uniqueIds },
    pageNumber,
    pageSize,
  );
}
